import { type IMazeOptions } from "utilities/thirdPartyIntegrations/maze/maze.types";

const MAZE_LOADER_URL = "https://snippet.maze.co/maze-universal-loader.js";
const MAZE_SCRIPT_ID = "bt-maze-universal-loader";

/**
 * Maze wrapper instance — loads the universal snippet used to show usability-test
 * instructions as an in-page overlay. Be sure to call `initialize()` to init Maze.
 * @param context {@link IMazeOptions} context
 * @returns Wrapper instance for the Maze integration
 */
export function MazeIntegration(context: IMazeOptions) {
    return {
        name: "maze",
        config: context,
        initialize: () => {
            if (document.getElementById(MAZE_SCRIPT_ID)) {
                return;
            }

            // Maze keys its session off this timestamp so a test spans page navigations.
            // BTSessionStorage can't back this: it mangles keys to `bt-<type>-<name>` and
            // JSON-encodes values, but the `maze-us` key and its raw value are Maze's
            // contract, not ours. Access is guarded because reading sessionStorage throws
            // outright (rather than returning null) when storage is blocked.
            let sessionStamp: string | null = null;
            try {
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- Maze owns this key/format; BTSessionStorage would rewrite both
                sessionStamp = window.sessionStorage.getItem("maze-us");
            } catch {
                // storage unavailable — Maze falls back to a per-page session
            }

            if (!sessionStamp) {
                sessionStamp = new Date().getTime().toString();
                try {
                    // eslint-disable-next-line @typescript-eslint/no-deprecated -- see above
                    window.sessionStorage.setItem("maze-us", sessionStamp);
                } catch {
                    // storage unavailable — see above
                }
            }

            const s = document.createElement("script");
            s.id = MAZE_SCRIPT_ID;
            s.type = "text/javascript";
            s.src = `${MAZE_LOADER_URL}?apiKey=${context.mazeApiKey}`;
            s.async = true;

            // Maze's own snippet reads the nonce off `document.currentScript`, which is
            // null inside a module. Copy it from an existing script instead so the loader
            // still satisfies a nonce-based CSP if one is ever adopted.
            const nonceSource = document.querySelector<HTMLScriptElement>("script[nonce]");
            if (nonceSource?.nonce) {
                s.setAttribute("nonce", nonceSource.nonce);
            }

            document.getElementsByTagName("head")[0].appendChild(s);
            window.mazeUniversalSnippetApiKey = context.mazeApiKey;
        },
    };
}
